import { html, TemplateResult } from "lit-html";
import { I18nService } from "Services/I18nService";
import { replacePlaceHolder } from "Helper/stringHelper";
import { shopApiResponse, shopRowInformation } from "Interfaces/shopApiResponse";

/**
 * Template for shop accordion
 *
 * Renders JSON data from API w/ lit-html
 */

/** ####### HELPERS ####### */

/** ####### RENDERER ####### */
/**
 * Render location image
 *
 * @param {shopRowInformation} row
 * @returns {TemplateResult}
 */
function renderImage(row: shopRowInformation): TemplateResult {
  if (row.locationInfo.logo) {
    return html`
      <fra-e-lazy-image
        class="fra-e-lazy-image fra-js-lazy-image fra-state-initialized fra-state-invp fra-state-loaded"
        src="${row.locationInfo.logo.logoUrl}"
        alt="${row.locationInfo.title}"
        srcset=""
        sizes=""
        aspect-ratio=""
        wrapper=""
      ></fra-e-lazy-image>
    `;
  }
  return html``;
}

/**
 * Render opening hours
 *
 * @param {shopRowInformation} row
 * @returns {TemplateResult}
 */
function renderOpeningHours(row: shopRowInformation): TemplateResult {
  if (row.locationInfo.openingHours) {
    const openingHours = row.locationInfo.openingHours;
    const days = openingHours[0].startAndEndDate;
    const time = openingHours[0].openAndClosingTime;

    return html`
      ${days} ${time}
    `;
  }
  return html``;
}

/**
 * Render area
 *
 * @param {shopRowInformation} row
 * @returns {TemplateResult}
 */
function renderArea(row: shopRowInformation): TemplateResult {
  return html`
    ${replacePlaceHolder(
      I18nService.getInstance().getKey("fraport.travel.components.content.shopOverview.accordion.row.area"),
      row.locationInfo.zone?.terminal,
      row.locationInfo.zone?.area,
      row.locationInfo.zone?.level,
    )}
  `;
}

/** ####### EXPORTS ####### */

/**
 * Renders one row of shop-item
 *
 * @param {Array<shopRowInformation>} row for shop accordion items
 * @returns {TemplateResult}
 */
export const shopRowTemplate = (row: shopRowInformation): TemplateResult => {
  return html`
    <dl class="fra-m-shop-accordion__list-item">
      <dd class="fra-m-shop-accordion__item-logo">${renderImage(row)}</dd>
      <dd class="fra-m-shop-accordion__item-name">
        <span class="fra-m-shop-accordion__mobile-label">
          ${I18nService.getInstance().getKey("de.fraport.travel.components.locationInfo.name.label")}
        </span>
        ${row.locationInfo.title}
      </dd>
      <dd class="fra-m-shop-accordion__item-area">
        <span class="fra-m-shop-accordion__mobile-label">
          ${I18nService.getInstance().getKey("de.fraport.travel.components.locationInfo.zone.area.label")}
        </span>
        ${renderArea(row)}
      </dd>
      <dd class="fra-m-shop-accordion__item-opening-hours">
        <span class="fra-m-shop-accordion__mobile-label">
          ${I18nService.getInstance().getKey("de.fraport.travel.components.locationInfo.openingHours.label")}
        </span>
        ${renderOpeningHours(row)}
      </dd>
      <dd class="fra-m-shop-accordion__item-link">
        <a
          class="fra-e-link
          fra-e-link--with-icon
          fra-e-link--animated-right
          "
          href="${row.locationInfo.url}"
          target="_blank"
        >
          <span class="fra-e-link__label"></span>
          <fra-e-icon class="fra-e-icon " icon-id="fra-icon-arrow-right"></fra-e-icon>
        </a>
      </dd>
    </dl>
  `;
};

/**
 * Renders section of shop-accordion
 *
 * @param {String} label
 * @param {Array<shopApiResponse>} section Collected shop info
 * @param {FraAccordion} accordionWrapper
 * @returns {TemplateResult}
 */
export const accordionSectionTemplate = (label: string, section: Array<shopRowInformation>): TemplateResult => {
  const rows = section.map(row => shopRowTemplate(row));

  return html`
    <div class="fra-m-accordion__section fra-js-accordion__section">
      <a href="#" class="fra-m-accordion__toggle fra-js-accordion__toggle">
        <span class="fra-m-accordion__toggle-label">${label}</span>
        <fra-e-icon class="fra-e-icon fra-m-accordion__toggle-icon" icon-id="fra-icon-chevron-down"></fra-e-icon>
      </a>
      <div class="fra-m-accordion__section-content">
        <dl class="fra-m-shop-accordion__list-head">
          <dd class="fra-m-shop-accordion__head-name">
            ${I18nService.getInstance().getKey("de.fraport.travel.components.locationInfo.name.label")}
          </dd>
          <dd class="fra-m-shop-accordion__head-area">
            ${I18nService.getInstance().getKey("de.fraport.travel.components.locationInfo.zone.area.label")}
          </dd>
          <dd class="fra-m-shop-accordion__head-opening-hours">
            ${I18nService.getInstance().getKey("de.fraport.travel.components.locationInfo.openingHours.label")}
          </dd>
        </dl>
        ${rows}
      </div>
    </div>
  `;
};

/**
 * Renders shop-accordion
 *
 * @param {Array<shopApiResponse>} shopData for shop accordion items
 * @param {FraAccordion} accordionWrapper
 * @returns {TemplateResult}
 */
export const shopAccordionTemplate = (shopData: shopApiResponse): TemplateResult[] | TemplateResult => {
  if (!shopData.locationInfoMap) return html``;

  return Object.entries(shopData.locationInfoMap).map(([key, section]) => accordionSectionTemplate(key, section));
};
